import { Radar, MessageSquare, BarChart3, User, LineChart, MoreHorizontal, Flag, Clapperboard, Users as UsersIcon, Share2, X, Radio, Megaphone, Shield, Handshake, LogOut } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useLanguage } from "@/hooks/useLanguage";
import { openAdvancedStats } from "@/lib/advancedStats";
import { useEffect, useState } from "react";
import { setBottomChannel } from "@/lib/pr28/bottomLayoutManager";
import { setLayerActive } from "@/lib/pr30/zeroOverlapPolicy";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/audit";

export type MobileTab = "radar" | "chat" | "stats" | "profile";

interface BottomNavProps {
  active: MobileTab;
  onChange: (t: MobileTab) => void;
}

export function BottomNav({ active, onChange }: BottomNavProps) {
  const { t } = useLanguage();
  const [moreOpen, setMoreOpen] = useState(false);
  const { isAuthenticated, logout } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const handleLogout = async () => {
    if (signingOut) return;
    setSigningOut(true);
    void trackEvent("user_control_hub_logout", { surface: "bottom_nav_more" });
    try { await logout(); } catch { /* ignore */ }
    try {
      for (const k of ["ss2026_registered", "ss2026_session_id", "ss2026_role"]) {
        window.localStorage.removeItem(k);
      }
    } catch { /* ignore */ }
    setMoreOpen(false);
    window.location.replace("/welcome");
  };
  useEffect(() => {
    setBottomChannel("bottom_nav", true);
    setLayerActive("bottom_nav", true);
    return () => {
      setBottomChannel("bottom_nav", false);
      setLayerActive("bottom_nav", false);
    };
  }, []);
  useEffect(() => {
    setBottomChannel("profile_quick", active === "profile");
  }, [active]);
  const items: { id: MobileTab; label: string; icon: typeof Radar }[] = [
    { id: "radar", label: t("nav_radar"), icon: Radar },
    { id: "chat", label: t("nav_chat"), icon: MessageSquare },
    { id: "stats", label: t("nav_stats"), icon: BarChart3 },
    { id: "profile", label: t("nav_profile"), icon: User },
  ];

  return (
    <>
      {moreOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" onClick={() => setMoreOpen(false)}>
          <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" />
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-[calc(env(safe-area-inset-bottom)+72px)] left-3 right-3 rounded-2xl glass border border-border/60 p-3 shadow-2xl animate-in slide-in-from-bottom-4"
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Más</span>
              <button onClick={() => setMoreOpen(false)} aria-label="Cerrar" className="h-7 w-7 grid place-items-center rounded-full hover:bg-accent">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { openAdvancedStats(); setMoreOpen(false); }}
                className="flex items-center gap-2 rounded-xl border border-border/60 bg-card/60 px-3 py-2.5 text-[12px] font-bold hover:bg-accent"
              >
                <LineChart className="h-4 w-4 text-primary" />
                <span className="truncate">{t("nav_advanced")}</span>
              </button>
              <MoreLink to="/creator-studio" icon={<Clapperboard className="h-4 w-4 text-primary" />} label="Creator" onClose={() => setMoreOpen(false)} />
              <MoreLink to="/social" icon={<UsersIcon className="h-4 w-4 text-[oklch(0.82_0.24_145)]" />} label="Social" onClose={() => setMoreOpen(false)} />
              <MoreLink to="/viral-cards" icon={<Share2 className="h-4 w-4 text-pink-400" />} label="Viral Cards" onClose={() => setMoreOpen(false)} />
              <MoreLink to="/media-blitz" icon={<Radio className="h-4 w-4 text-cyan-400" />} label="Media Blitz" onClose={() => setMoreOpen(false)} />
              <MoreLink to="/ambassadors" icon={<Megaphone className="h-4 w-4 text-[oklch(0.78_0.18_70)]" />} label="Embajadores" onClose={() => setMoreOpen(false)} />
              <MoreLink to="/partners" icon={<Handshake className="h-4 w-4 text-[oklch(0.78_0.20_50)]" />} label="Partners" onClose={() => setMoreOpen(false)} />
              <MoreLink to="/admin" icon={<Shield className="h-4 w-4 text-[oklch(0.70_0.20_270)]" />} label="Admin" onClose={() => setMoreOpen(false)} />
              {isAuthenticated && (
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={signingOut}
                  className="flex items-center gap-2 rounded-xl border border-destructive/50 bg-destructive/10 px-3 py-2.5 text-[12px] font-bold text-destructive hover:bg-destructive/20 disabled:opacity-60"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="truncate">{signingOut ? "Cerrando…" : "Cerrar sesión"}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    <nav
      aria-label="Mobile navigation"
      data-testid="bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-border/60 glass lg:hidden"
    >
      <ul className="mx-auto flex max-w-md items-stretch justify-around px-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2">
        {items.map((it) => {
          const Active = active === it.id;
          return (
            <li key={it.id} className="flex-1">
              <button
                onClick={() => onChange(it.id)}
                className={`flex min-h-[44px] w-full flex-col items-center justify-center gap-0.5 rounded-xl py-2 text-[10px] font-semibold transition ${
                  Active ? "text-primary bottom-nav-team-active" : "text-muted-foreground hover:text-foreground"
                }`}
                aria-current={Active ? "page" : undefined}
              >
                <it.icon className={`h-5 w-5 ${Active ? "drop-shadow-team" : ""}`} />
                {it.label}
              </button>
            </li>
          );
        })}
        {/* GO_LIVE_P0 — surface Nation Wars in primary nav; Advanced Stats moved into Más. */}
        <li className="flex-1">
          <Link
            to="/nation-wars"
            className="flex min-h-[44px] w-full flex-col items-center justify-center gap-0.5 rounded-xl py-2 text-[10px] font-semibold text-amber-400 transition hover:text-foreground"
            aria-label="Nation Wars"
          >
            <Flag className="h-5 w-5" />
            Naciones
          </Link>
        </li>
        <li className="flex-1">
          <button
            onClick={() => setMoreOpen((v) => !v)}
            className={`flex min-h-[44px] w-full flex-col items-center justify-center gap-0.5 rounded-xl py-2 text-[10px] font-semibold transition ${moreOpen ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
            aria-label="Más"
            aria-expanded={moreOpen}
          >
            <MoreHorizontal className="h-5 w-5" />
            Más
          </button>
        </li>
      </ul>
    </nav>
    </>
  );
}

function MoreLink({ to, icon, label, onClose }: { to: string; icon: React.ReactNode; label: string; onClose: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className="flex items-center gap-2 rounded-xl border border-border/60 bg-card/60 px-3 py-2.5 text-[12px] font-bold hover:bg-accent"
    >
      {icon}
      <span className="truncate">{label}</span>
    </Link>
  );
}